import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Gift,
  CheckCircle,
  ExternalLink,
  Star,
  Zap,
  Clock,
  AlertCircle,
  XCircle
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface BetfredSignupProps {
  competitionId: number;
  title: string;
}

interface BetfredSettings {
  enabled: boolean;
  required: boolean;
  entriesAwarded: number;
}

interface BetfredSignupData {
  hasSignedUp: boolean;
  signupDate?: string;
  verified: boolean;
  entriesAwarded: number;
  verificationStatus?: string;
  verificationAttempts?: number;
  betfredEmail?: string;
  id?: number;
}

export default function BetfredSignup({ competitionId, title }: BetfredSignupProps) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Query Betfred settings for this specific competition (public endpoint for enabled status)
  const { data: publicSettings } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/betfred-settings", competitionId],
  });

  // Query full Betfred settings for admin features (entries awarded, etc.)
  const { data: adminSettings } = useQuery<BetfredSettings>({
    queryKey: ["/api/admin/betfred-settings"],
    enabled: false, // Disabled for now - using public settings
  });

  // Use admin settings if available, otherwise use public settings with defaults
  const settings = adminSettings || (publicSettings ? {
    enabled: publicSettings.enabled,
    required: false,
    entriesAwarded: 50
  } : null);

  // Query user's Betfred signup status for this competition
  const { data: signupData } = useQuery<BetfredSignupData>({
    queryKey: ["/api/betfred-signup", competitionId],
    enabled: isAuthenticated,
  });

  // Query global Betfred verification status (if user has ever been verified)
  const { data: globalStatus } = useQuery<{ hasBeenVerified: boolean }>({
    queryKey: ["/api/betfred-global-status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    staleTime: 0, // Always fresh data
    refetchOnWindowFocus: true,
  });


  const signupMutation = useMutation({
    mutationFn: async (data: { email: string }) => {
      const payload = {
        competitionId,
        betfredEmail: data.email,
      };

      console.log("🔵 [BETFRED COMPETITION] Sending request with payload:", payload);
      const response = await apiRequest("POST", `/api/betfred-signup`, { body: payload });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Email Submitted Successfully!",
        description: data.message || "Verification will begin in 30 minutes. Check back to see if your email is registered with Betfred.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/betfred-signup", competitionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
      setEmail("");
    },
    onError: (error: any) => {
      let title = "Signup Failed";
      let description = error.message || "Failed to record Betfred signup";

      // Handle the 409 status code (already registered) with user-friendly message
      if (error.status === 409 || error.message?.includes('already registered')) {
        title = "Already Registered";
        description = "You have already registered your email for this competition. Your verification is in progress or completed.";
      } else if (error.status === 400) {
        title = "Invalid Information";
        description = "Please check your email address and try again.";
      }

      toast({
        title,
        description,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (data: { email?: string }) => {
      const payload = {
        competitionId,
        betfredEmail: data.email || null,
      };

      const response = await apiRequest("POST", `/api/betfred-signup/retry`, { body: payload });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Retry Scheduled!",
        description: data.message || "Verification retry scheduled. Check back in a minute.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/betfred-signup", competitionId] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard"] });
      setEmail("");
    },
    onError: (error: any) => {
      toast({
        title: "Retry Failed",
        description: error.message || "Failed to retry verification",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your Betfred email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    signupMutation.mutate({ email: email.trim() });
  };

  const handleRetry = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Use new email if provided, otherwise retry with same email
    retryMutation.mutate({ email: email.trim() || undefined });
  };

  // Don't show if not authenticated (but treat undefined as loading state)
  if (!isAuthenticated && !user) {
    return null;
  }

  // Don't show if disabled by admin
  if (settings && !settings.enabled) {
    return null;
  }

  // Show completed state only if user has been verified for THIS specific competition
  if (signupData?.hasSignedUp && signupData?.verified) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg text-green-800">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Betfred Bonus Completed
          </CardTitle>
          <p className="text-sm text-green-700">
            You have successfully claimed your Betfred bonus entries for this competition.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Bonus Entries Awarded</span>
            </div>
            <Badge variant="secondary" className="bg-green-200 text-green-800">
              +{signupData.entriesAwarded} entries
            </Badge>
          </div>

          <div className="text-sm text-green-600 space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Email verified with Betfred: {signupData.betfredEmail}</span>
            </div>
            <p className="text-xs text-green-500 italic">
              Bonus entries have been added to your total for this competition.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show status for non-verified signups (pending, failed, invalid)
  if (signupData?.hasSignedUp && !signupData?.verified) {
    const isPending = signupData.verificationStatus === 'pending';
    const isInvalid = signupData.verificationStatus === 'invalid';
    const isFailed = signupData.verificationStatus === 'failed';
    const attempts = signupData.verificationAttempts || 0;
    const canRetry = isInvalid || isFailed;

    return (
      <Card className={`border-2 ${isPending ? 'border-blue-200 bg-blue-50' : 'border-orange-200 bg-orange-50'}`}>
        <CardHeader className="pb-4">
          <CardTitle className={`flex items-center gap-2 text-lg ${isPending ? 'text-blue-800' : 'text-orange-800'}`}>
            {isPending ? <Clock className="h-5 w-5 text-blue-600" /> :
             <AlertCircle className="h-5 w-5 text-orange-600" />}
            {isPending ? 'Betfred Verification in Progress...' :
             'Betfred Verification Issue'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email Display - Grayed out if pending */}
          <div className="p-3 bg-gray-100 rounded-lg border">
            <div className="text-xs text-gray-500 mb-1">Your Betfred Email:</div>
            <div className={`text-sm font-medium ${isPending ? 'text-gray-500' : 'text-gray-800'}`}>
              {signupData.betfredEmail}
            </div>
          </div>

          {/* Status Display */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${isPending ? 'bg-blue-100 border-blue-200' : 'bg-orange-100 border-orange-200'}`}>
            <div className="flex items-center gap-2">
              <Gift className={`h-4 w-4 ${isPending ? 'text-blue-600' : 'text-orange-600'}`} />
              <span className={`text-sm font-medium ${isPending ? 'text-blue-700' : 'text-orange-700'}`}>
                {isPending ? 'Bonus Entries Pending...' : 'Verification Failed'}
              </span>
            </div>
            <Badge variant="secondary" className={`${isPending ? 'bg-blue-200 text-blue-800' : 'bg-orange-200 text-orange-800'}`}>
              {isPending ? `+50 entries pending` : `No entries awarded`}
            </Badge>
          </div>

          <div className="text-sm space-y-3">

            {isPending && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-blue-600">
                  <Clock className="h-4 w-4 animate-spin" />
                  <span>Verification in progress - this may take up to 30 minutes</span>
                </div>
                <div className="text-xs text-blue-500 italic">
                  Please check back later. Your email field is temporarily disabled during verification.
                </div>
              </div>
            )}

            {isInvalid && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-4 w-4" />
                  <span>Email not found in Betfred system</span>
                </div>
                <div className="text-xs text-red-500 leading-relaxed">
                  Your email address was not found in Betfred's customer database. Please ensure you've registered with Betfred first, or try a different email address.
                </div>
              </div>
            )}

            {isFailed && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertCircle className="h-4 w-4" />
                  <span>Verification failed due to technical error</span>
                </div>
                <div className="text-xs text-orange-500 leading-relaxed">
                  A technical issue occurred during verification. This could be a temporary problem with the Betfred API.
                </div>
              </div>
            )}

            {/* Retry Section */}
            {canRetry && (
              <div className="pt-3 border-t space-y-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-2">Retry Verification Options</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    You can retry as many times as needed
                  </p>
                </div>

                {/* Quick retry with same email */}
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-blue-900 mb-2">Retry with Same Email:</div>
                    <div className="text-xs text-blue-700 mb-3">
                      Retry verification using: {signupData.betfredEmail}
                    </div>
                    <Button
                      onClick={() => {
                        setIsSubmitting(true);
                        retryMutation.mutate({ email: signupData.betfredEmail });
                      }}
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="button-quick-retry"
                    >
                      {isSubmitting ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Retry with Same Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="text-center text-gray-400">
                  <span className="text-sm">OR</span>
                </div>

                {/* Try with different email */}
                <div className="space-y-3">
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="text-sm font-medium text-orange-900 mb-2">Try with Different Email:</div>
                    <form onSubmit={handleRetry} className="space-y-3">
                      <Input
                        type="email"
                        placeholder="Enter your Betfred email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSubmitting}
                        className="w-full"
                        data-testid="input-retry-email"
                      />
                      <Button
                        type="submit"
                        disabled={isSubmitting || !email.trim()}
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="button-retry-different-email"
                      >
                        {isSubmitting ? "Scheduling Retry..." : `Retry with Different Email`}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {!signupData.verificationStatus && (
              <div className="flex items-center gap-2 text-yellow-600">
                <Clock className="h-4 w-4" />
                <span>Verification scheduled - Check back in 30 minutes for results</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg text-blue-900">
          <Star className="h-5 w-5 text-blue-900" />
          Betfred Bonus
          {settings?.required && (
            <Badge variant="destructive" className="ml-2">Required</Badge>
          )}
        </CardTitle>
        <p className="text-sm text-blue-900">
          Sign up to Betfred and earn {settings?.entriesAwarded || 50} bonus entries for this competition!
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Benefits */}
        <div className="flex items-center justify-between p-3 bg-blue-100 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-blue-900" />
            <span className="text-sm font-medium text-purple-700">Bonus Reward!</span>
          </div>
          <Badge variant="secondary" className="bg-purple-200 text-blue-900">
            +{settings?.entriesAwarded || 50} entries
          </Badge>
        </div>

        {/* Instructions */}
        <div className="space-y-2 text-sm text-purple-700">
          <p className="font-medium">How it works:</p>
          <ol className="list-decimal list-inside space-y-1 text-blue-900">
            <li>Click the button below to open registration</li>
            <li>Complete your account registration</li>
            <li>Enter your signup email in the form below</li>
            <li>Wait up to 30 minutes for automatic verification</li>
          </ol>
        </div>

        {/* Betfred Signup Button */}
        <div className="space-y-3">
          <Button
            onClick={() => window.open('https://betfredsa.click/o/3F2EJM', '_blank', 'noopener,noreferrer')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105"
            size="lg"
          >
            <ExternalLink className="mr-2 h-5 w-5" />
            Sign Up with Betfred
            <span className="ml-2 text-purple-200">→</span>
          </Button>

          <p className="text-xs text-blue-900 text-center">
            Opens in a new tab • Must be 18+ • Terms and conditions apply
          </p>

          <form onSubmit={signupData?.hasSignedUp ? handleRetry : handleSubmit} className="space-y-3">
            <div className="space-y-2">
              <label htmlFor="betfred-email" className="text-sm font-medium text-purple-700">
                Enter your Betfred signup email:
              </label>
              <Input
                id="betfred-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="border-purple-200 focus:border-purple-400"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmitting ? "Recording..." : "Claim Bonus Entries"}
            </Button>
          </form>
        </div>

        <p className="text-xs text-purple-500 text-center">
          Automatic verification can take up to 30 minutes.
        </p>
      </CardContent>
    </Card>
  );
}